/* =========================================================================
 * CHEER AND DANCE — THE LARGEST GOVERNING BODY IN THE OBJECT HAD NO CARD.
 *
 * Measured against the live object on 2026-09-21, not assumed:
 *
 *   Varsity Spirit                        1,354 records
 *   California Interscholastic Federation 1,138 records
 *
 * Varsity Spirit is the single largest `sr_conference` value SR holds —
 * larger than CIF, larger than the NCAA (275), larger than any state
 * association — and it was not on this board in any form. The only occurrence
 * of the string anywhere in the client bundle was a SPORT LABEL inside a
 * TSSAA entry. 2,228 cheer and dance records exist; 1,936 of them rendered
 * nowhere at all, and the 292 that did only appeared because they sit under a
 * state association that is on the board for other reasons.
 *
 * ⚠️ WHY ITS OWN TAB RATHER THAN A HOME ON AN EXISTING ONE. Varsity Spirit's
 * 1,354 records break down by `sr_segment` as All-Star 906, Dance 353, HS
 * State 89, Cheer/Dance (College) 6. One body crowns at four levels at once,
 * so every existing tab is the wrong tab for it and filing it under any of
 * them would fragment the largest body in the object across three boards.
 *
 * The precedent is the Professional pseudo-state, whose own note reads "we
 * already hold 61 records at sr_level = Professional with nowhere on the board
 * to look them up". 61 records earned a tab. This is 1,641.
 *
 * ⚠️ MATCHED BY `aliases`, NOT BY ACRONYM, AND THAT IS LOAD-BEARING.
 * `\bVARSITY\b` was measured against the object before being rejected: it hits
 * 1,421 records, of which 67 belong to CIF (26), MHSAA (19), TSSAA (16) and
 * WVSSAC (3) — state records whose championship_name says "Varsity
 * Cheerleading". Matching this body on its acronym would have quietly stolen
 * 67 records off four state cards, which is the Arkansas "Class AAA" defect
 * exactly. Every body here matches on a verified full label in the
 * `sr_conference` field and on nothing else.
 *
 * ⚠️ THE DUPLICATE LABELS ARE CARRIED AS ALIASES, NOT MERGED. The object holds
 * "USASF" (52) and "U.S. All Star Federation (USASF)" (2) as separate values,
 * and "Varsity Spirit" (1,354) beside "Varsity Spirit — The Quest" (1).
 * Collapsing those in the data is a record merge and needs a data-repair
 * permission nobody has granted, so the registry reads both labels onto the
 * correct card and the records stay exactly as they are.
 *
 * ⚠️ MARKS ARE NOT HOT-LINKED. `logoSource` is a CANDIDATE the backend fetches
 * and hosts in HubSpot File Manager — owned-assets, the same rule as the school
 * crests and the pro leagues. Varsity Brands enforces its marks aggressively.
 * Left null rather than guessed.
 *
 * Same entity shape as the state, college, Canada and pro registries on
 * purpose, so allEntities(), markFor(), the hosted-mark pipeline and the
 * body↔record matcher all work on these with no second implementation.
 * ========================================================================= */

const B = (acronym, name, site, logoSource, champs, kind, extra = {}) => ({
  acronym, name, site, logoSource, champs, kind, ...extra,
})

export const CHEER_BODIES = [
  {
    st: 'SPIRIT', state: 'Cheer and Dance', acronym: 'SPIRIT', level: 'National',
    name: 'Cheer and dance sanctioning bodies',
    /* ⚠️ THIS CARD IS A ROLL-UP, NOT A GOVERNING BODY, AND SAYING SO IS A FIX.
       Brian, on the live board: "Varsity and Varsity Spirit both have a
       different count and there might be a duplicate."

       He was right and the cause was mine. Every other entry here is a real
       organisation; this one is the tab's heading, with a name
       ("Cheer and dance sanctioning bodies") that matches no record. It
       survived purely on its acronym matching the WORD "spirit" in the
       governing-body field — which read 1,354 while Varsity Spirit read 1,355,
       and then read 1,454 the moment I filled 99 records with
       "Worldwide Spirit Association (WSA)" last deploy. A different producer
       entirely, pulled onto a card by a coincidence of vocabulary.

       A roll-up counts its children and matches nothing on its own, so it can
       never disagree with them again. */
    rollup: true,
    site: null, accent: '#6D28D9', logoSource: null, champs: null,
    note: '⚠️ THE LARGEST BODY IN SR’S ENTIRE OBJECT IS ON THIS TAB. Varsity Spirit holds 1,354 championship records against California Interscholastic Federation’s 1,138, and until now it had no card anywhere on this board. A cheer body has NO STATE — it sanctions across all of them — so every count here reads the body’s own records and never a state audit.',
    others: [
      /* ⚠️ THE NAME IS DELIBERATELY LONGER THAN THE LABEL. The matcher treats a
         body's `name` appearing in a championship TITLE as a strong signal, and
         a bare "Varsity Spirit" as the name would drag in the one TSSAA record
         whose title says "…Varsity Spirit…" — a record TSSAA sanctions, not
         this body. Naming the organisation in full makes the title rule unable
         to fire by accident and leaves the `aliases` list, which reads only the
         governing-body field, as the single way onto this card. */
      B('Varsity Spirit', 'Varsity Spirit, a Varsity Brands company', 'https://www.varsity.com/', null,
        'https://www.varsity.com/events/', 'cheer and dance', {
          level: 'National',
          aliases: ['Varsity Spirit'],
          note: '⚠️ THE SINGLE LARGEST GOVERNING BODY IN THE OBJECT — 1,354 records, more than any state association. It crowns at four levels simultaneously (All-Star 906, Dance 353, HS State 89, College Cheer/Dance 6), which is why cheer needed its own tab rather than a row on an existing one. ⚠️ NCA, UCA, UDA AND NDA ALL SIT UNDER THIS CARD, AND THAT IS MEASURED, NOT ASSUMED: 759 records name one of those four brands as a whole word, and all 759 already carry sr_conference = Varsity Spirit. There is no stray to chase and nothing to reassign. ⚠️ "Varsity Spirit — The Quest" is a SECOND LABEL for the same organisation, carried as an alias; the record is not merged.',
        }),
      B('CANAM', 'Cheer Ltd. (CANAM)', 'https://www.cheerltd.com/', null,
        'https://www.cheerltd.com/events', 'cheer', {
          level: 'National',
          aliases: ['Cheer Ltd. (CANAM)', 'Cheer Ltd.'],
          note: 'The CANAM Nationals organiser — 122 records, the second-largest cheer body we hold.',
        }),
      B('OCS', 'The Open Championship Series', null, null, null, 'cheer', {
          level: 'National',
          aliases: ['The Open Championship Series'],
          noSite: true,
          note: '⚠️ A BODY SR DID NOT KNOW IT HELD. 100 records sat under this label with no card, no tab and no mention anywhere in the client bundle. ⚠️ No website is recorded — it exists in our object and is currently unlinkable, which is a worklist item and not a reason to leave it off the board.',
        }),
      B('USASF', 'U.S. All Star Federation', 'https://www.usasf.net/', null,
        'https://www.usasf.net/worlds', 'all-star cheer and dance', {
          level: 'National',
          aliases: ['USASF', 'U.S. All Star Federation'],
          note: 'The all-star sanctioning body behind The Cheerleading Worlds and Dance Worlds — 54 records across two labels. ⚠️ Matched on the ALIAS LIST rather than the acronym: 52 records say only "USASF" while 2 say "U.S. All Star Federation (USASF)", and the registry reads both onto this card without merging either.',
        }),
      B('USA Cheer', 'USA Cheer', 'https://www.usacheer.org/', null,
        'https://www.usacheer.org/stunt', 'STUNT', {
          level: 'National',
          aliases: ['USA Cheer'],
          note: 'The national governing body for sport cheering, and the sanctioning body for STUNT — 5 records, all recorded as "USA Cheer (STUNT)".',
        }),
      B('Cheer Canada', 'Cheer Canada', 'https://www.cheercanada.ca/', null,
        'https://www.cheercanada.ca/nationals', 'cheer', {
          level: 'National',
          aliases: ['Cheer Canada'],
          note: '⚠️ A CANADIAN BODY, DELIBERATELY ON THIS TAB AND NOT THE CANADA TAB. The Canada tab holds U SPORTS and the CCAA — university and college sport. Cheer Canada is the national cheer body across every level, so it belongs with the other cheer sanctioning bodies; splitting cheer by country is what left it invisible.',
        }),
      B('ICU', 'International Cheer Union', 'https://cheerunion.org/', null,
        'https://cheerunion.org/worldchampionships/', 'cheer', {
          level: 'International',
          aliases: ['International Cheer Union'],
          multiState: true,
          note: '⚠️ AN INTERNATIONAL TITLE IS NOT A STATE OR NATIONAL ONE. The ICU World Championships are contested by national teams, so a winner here did not win an American championship — the same distinction the multi-state flag carries on the high-school tab.',
        }),
    ],
  },
]

/* ⚠️ THE LABEL THAT NAMES NO BODY. 292 cheer and dance records carry
 * `sr_conference = 'Unspecified'`, which is not an organisation and can never
 * be given a card — no registry entry can fix a record that does not say who
 * sanctioned it. They are the cheer equivalent of the "(state missing)"
 * orphans on the high-school tab and they get the same treatment: a door on
 * this tab that opens them, rather than a number nobody can reach or, worse,
 * silence. Resolving them is a data repair and needs a human. */
export const CHEER_UNSPECIFIED = 'Unspecified'
